// WP-1.10 (M13 RC-1): the recurring schedule builder — pure calendar math, no engine state.
// Every function is deterministic given its inputs (AR-7: no reading the clock); callers
// always pass an explicit reference instant. Dates are UTC throughout, matching how `at`/
// `dueDate` are stored everywhere else in the engine.

export const RECUR_FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'] as const;
export type RecurFrequency = (typeof RECUR_FREQUENCIES)[number];

export type MonthlyRule =
  | { kind: 'day'; day: number } // 1-31; clamped to the real last day of a shorter month
  | { kind: 'firstWeekday'; weekday: number } // 0=Sun..6=Sat — first match in the target month
  | { kind: 'lastDay' };

export interface RecurSchedule {
  frequency: RecurFrequency;
  everyN: number; // "every-N-periods" — 1 means every period
  /** monthly/yearly only; defaults to a day-of-month rule matching the reference date. */
  monthlyRule?: MonthlyRule;
  /** weekly only; defaults to the reference date's own weekday. */
  weekday?: number;
}

function daysInMonth(year: number, monthIndex0: number): number {
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
}

function applyMonthlyRule(year: number, monthIndex0: number, rule: MonthlyRule): Date {
  if (rule.kind === 'lastDay') {
    return new Date(Date.UTC(year, monthIndex0, daysInMonth(year, monthIndex0)));
  }
  if (rule.kind === 'day') {
    const day = Math.min(rule.day, daysInMonth(year, monthIndex0));
    return new Date(Date.UTC(year, monthIndex0, day));
  }
  // firstWeekday
  const first = new Date(Date.UTC(year, monthIndex0, 1));
  const offset = (rule.weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, monthIndex0, 1 + offset));
}

/** The smallest valid occurrence strictly after `afterIso`. */
export function nextOccurrence(schedule: RecurSchedule, afterIso: string): string {
  const after = new Date(afterIso);
  const everyN = schedule.everyN > 0 ? schedule.everyN : 1;

  if (schedule.frequency === 'daily') {
    const next = new Date(after);
    next.setUTCDate(next.getUTCDate() + everyN);
    return next.toISOString();
  }

  if (schedule.frequency === 'weekly') {
    const weekday = schedule.weekday ?? after.getUTCDay();
    const next = new Date(after);
    next.setUTCDate(next.getUTCDate() + 1);
    while (next.getUTCDay() !== weekday) next.setUTCDate(next.getUTCDate() + 1);
    if (everyN > 1) next.setUTCDate(next.getUTCDate() + (everyN - 1) * 7);
    return next.toISOString();
  }

  if (schedule.frequency === 'monthly') {
    const rule: MonthlyRule = schedule.monthlyRule ?? { kind: 'day', day: after.getUTCDate() };
    let totalMonths = after.getUTCFullYear() * 12 + after.getUTCMonth();
    // Check the current period's own candidate first — e.g. "next lastDay after Jan 15"
    // is Jan 31, the same month, not a jump to February.
    let candidate = applyMonthlyRule(Math.floor(totalMonths / 12), totalMonths % 12, rule);
    while (candidate.getTime() <= after.getTime()) {
      totalMonths += everyN;
      candidate = applyMonthlyRule(Math.floor(totalMonths / 12), totalMonths % 12, rule);
    }
    return candidate.toISOString();
  }

  // yearly
  const rule: MonthlyRule = schedule.monthlyRule ?? { kind: 'day', day: after.getUTCDate() };
  const monthIndex0 = after.getUTCMonth();
  let year = after.getUTCFullYear();
  let candidate = applyMonthlyRule(year, monthIndex0, rule);
  while (candidate.getTime() <= after.getTime()) {
    year += everyN;
    candidate = applyMonthlyRule(year, monthIndex0, rule);
  }
  return candidate.toISOString();
}

/** The smallest valid occurrence at or after `fromIso` — `fromIso` itself if it already qualifies. */
export function firstOccurrenceOnOrAfter(schedule: RecurSchedule, fromIso: string): string {
  const from = new Date(fromIso);

  if (schedule.frequency === 'daily') return from.toISOString();

  if (schedule.frequency === 'weekly') {
    const weekday = schedule.weekday ?? from.getUTCDay();
    if (from.getUTCDay() === weekday) return from.toISOString();
    return nextOccurrence(schedule, new Date(from.getTime() - 86_400_000).toISOString());
  }

  if (schedule.frequency === 'monthly') {
    const rule: MonthlyRule = schedule.monthlyRule ?? { kind: 'day', day: from.getUTCDate() };
    const candidate = applyMonthlyRule(from.getUTCFullYear(), from.getUTCMonth(), rule);
    if (candidate.getTime() >= from.getTime()) return candidate.toISOString();
    return nextOccurrence(schedule, fromIso);
  }

  // yearly
  const rule: MonthlyRule = schedule.monthlyRule ?? { kind: 'day', day: from.getUTCDate() };
  const candidate = applyMonthlyRule(from.getUTCFullYear(), from.getUTCMonth(), rule);
  if (candidate.getTime() >= from.getTime()) return candidate.toISOString();
  return nextOccurrence(schedule, fromIso);
}

/** §18.5 "next-3 preview": `count` occurrences at/after `fromIso`, in order. */
export function generateOccurrences(
  schedule: RecurSchedule,
  fromIso: string,
  count: number,
): string[] {
  const occurrences: string[] = [];
  let cursor = firstOccurrenceOnOrAfter(schedule, fromIso);
  for (let i = 0; i < count; i++) {
    occurrences.push(cursor);
    cursor = nextOccurrence(schedule, cursor);
  }
  return occurrences;
}
